import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { WebSocketStatus } from '@numeo/types-core'

export interface WebSocketState {
    status: WebSocketStatus
    error: string | null
    // Flag to indicate the connection was forcefully terminated (e.g., duplicate session)
    // When true, automatic reconnection should be prevented
    forceDisconnected: boolean
    /**
     * True while replaying missed Agentic Hub events after a reconnect. Drives the "Updating..."
     * UI state between CONNECTED (socket reopen) and the moment the replay finishes populating
     * state from the Redis buffer.
     */
    isReplaying: boolean
    /**
     * Epoch ms when the socket entered DISCONNECTED (or ERROR) state. Cleared on CONNECTED.
     * Used by the UI to escalate the stale-state banner at 30s and 2 min thresholds.
     */
    disconnectedSince: number | null
    /**
     * True once the socket has successfully connected at least once in this session. Gates the
     * replay flow: on the very first SOCKET_CONNECTED we don't replay (nothing to catch up on),
     * but on every subsequent reconnect we do. Lives in Redux (not a module-level variable) so
     * it survives HMR during development and is not vulnerable to middleware re-initialization.
     */
    hasConnectedOnce: boolean
}

const initialState: WebSocketState = {
    status: WebSocketStatus.DISCONNECTED,
    error: null,
    forceDisconnected: false,
    isReplaying: false,
    disconnectedSince: null,
    hasConnectedOnce: false,
}

const webSocketSlice = createSlice({
    name: 'webSocket',
    initialState,
    reducers: {
        connecting(state) {
            state.status = WebSocketStatus.CONNECTING
            state.error = null
        },
        connected(state) {
            state.status = WebSocketStatus.CONNECTED
            state.error = null
            state.forceDisconnected = false // Reset on successful connection
            state.disconnectedSince = null
        },
        disconnected(state, action: PayloadAction<string | null>) {
            state.status = WebSocketStatus.DISCONNECTED
            state.error = action.payload
            if (state.disconnectedSince === null) {
                state.disconnectedSince = Date.now()
            }
        },
        // New action for force disconnect scenarios
        forceDisconnect(state, action: PayloadAction<string | null>) {
            state.status = WebSocketStatus.DISCONNECTED
            state.error = action.payload
            state.forceDisconnected = true
            if (state.disconnectedSince === null) {
                state.disconnectedSince = Date.now()
            }
        },
        error(state, action: PayloadAction<string>) {
            state.status = WebSocketStatus.ERROR
            state.error = action.payload
            if (state.disconnectedSince === null) {
                state.disconnectedSince = Date.now()
            }
        },
        replayStarted(state) {
            state.isReplaying = true
        },
        replayCompleted(state) {
            state.isReplaying = false
        },
        /**
         * Mark the session as having connected at least once. Idempotent — only transitions
         * false → true, never back. Dispatched from webSocketMiddleware on the first
         * SOCKET_CONNECTED or ALREADY_CONNECTED message.
         */
        markConnectedOnce(state) {
            state.hasConnectedOnce = true
        },
    },
})

export const { connecting, connected, disconnected, forceDisconnect, error, replayStarted, replayCompleted, markConnectedOnce } = webSocketSlice.actions
export default webSocketSlice.reducer
